import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, MenuItem } from '@mui/material';
import Chip from '@mui/material/Chip';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import AppButton from 'src/components/appComponents/AppButton';
import AppLoadingAndErrorWrapper from 'src/components/appComponents/AppLoadingAndErrorWrapper';
import FormBlock from 'src/components/appComponents/FormBlock';
import FormProvider, {
  RHFAutocomplete,
  RHFMultiCheckbox,
  RHFRadioGroup,
  RHFSelect,
  RHFTextField,
} from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import {
  useGetAllChannelsQuery,
  useGetChannelContentQuery,
  useGetParentSuggestionByIdQuery,
  usePublishParentSuggestionMutation,
} from 'src/graphql';
import { useParams, useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { PublishParentSuggestioSchema } from 'src/schema';
import { IPublishParentSuggestion } from 'src/types/parentSuggestion';
import { MUSIC_TYPE } from 'src/utils/appConst';

export default function PublishParentSuggestionForm({
  suggestionVideo,
  isLoadingGetSuggestionVideo,
}: any) {
  const router = useRouter();
  const { suggestedId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<IPublishParentSuggestion>({
    resolver: yupResolver(PublishParentSuggestioSchema),
  });

  const { data: channels, isLoading: isLoadingChannels } = useGetAllChannelsQuery();
  const { data: channelContent, isLoading: isLoadingChannelContent } = useGetChannelContentQuery({
    _eq: methods.watch()?.channelId,
  });

  const { mutate: publishVideo, isLoading: isLoadingPublishVideo } =
    usePublishParentSuggestionMutation({
      onSuccess: () => {
        enqueueSnackbar({ variant: 'success', message: 'Completed Successfully' });
        router.push(paths.dashboard.parentSuggestions.suggestionDetails(suggestedId ?? ''));
      },
    });

  const onSubmit = useCallback(async (data: IPublishParentSuggestion) => {
    const channelRelatedContents: any = data?.contentIds?.map((item: any) => item?.id);
    publishVideo({
      channelId: data?.channelId,
      classification: data?.classification && data?.classification[0],
      title: data?.title,
      description: data?.description,
      musicType: data?.musicType,
      contentIds: channelRelatedContents,
      suggestedId,
    });
  }, []);

  // For set default values
  useEffect(() => {
    if (!isLoadingGetSuggestionVideo && suggestionVideo?.app_suggested_by_pk) {
      const defaultValues: any = {
        title: {
          en: suggestionVideo?.app_suggested_by_pk?.title?.en,
          ar: suggestionVideo?.app_suggested_by_pk?.title?.ar,
        },
        description: {
          en: suggestionVideo?.app_suggested_by_pk?.description?.en,
          ar: suggestionVideo?.app_suggested_by_pk?.description?.ar,
        },
        classification: [suggestionVideo?.app_suggested_by_pk?.type],
        contentIds: [],
      };
      methods.reset(defaultValues);
    }
  }, [suggestionVideo, methods]);

  return (
    <AppLoadingAndErrorWrapper
      isLoading={isLoadingGetSuggestionVideo || isLoadingChannels}
      sx={{ height: 'auto' }}
    >
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container rowSpacing={3} alignItems="center">
          <FormBlock
            title="Channel Name*"
            inputField={
              <RHFSelect name="channelId" label="Select Channel Name">
                {channels?.channels?.map((item) => (
                  <MenuItem value={item?.id}>{item?.name?.en}</MenuItem>
                ))}
              </RHFSelect>
            }
          />
          <FormBlock
            title="Classification*"
            inputField={
              <RHFMultiCheckbox
                row
                name="classification"
                spacing={4}
                options={[
                  { value: 'short', label: 'Short' },
                  { value: 'video', label: 'Video' },
                ]}
              />
            }
          />

          <FormBlock
            title="English Title**"
            inputField={<RHFTextField name="title.en" label="Enter english title" />}
          />
          <FormBlock
            title="Arabic Title*"
            inputField={<RHFTextField name="title.ar" label="Enter arabic title" />}
          />
          <FormBlock
            title="English Description*"
            inputField={
              <RHFTextField name="description.en" label="Enter english short description" />
            }
          />
          <FormBlock
            title="Arabic Description*"
            inputField={
              <RHFTextField name="description.ar" label="Enter arabic short description" />
            }
          />
          <FormBlock
            title="Related Content*"
            inputField={
              <RHFAutocomplete
                name="contentIds"
                label="Choose content"
                multiple
                disableCloseOnSelect
                disabled={!methods.watch()?.channelId}
                options={channelContent?.contents ?? []}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                loading={isLoadingChannelContent}
                renderOption={(props, option: any) => (
                  <li {...props} key={option?.id}>
                    {option?.name?.en}
                  </li>
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option?.id}
                      label={option?.name?.en}
                      size="small"
                      color="primary"
                      variant="filled"
                    />
                  ))
                }
              />
            }
          />

          <FormBlock
            title="Music*"
            inputField={
              <RHFRadioGroup row spacing={3} name="musicType" options={MUSIC_TYPE ?? []} />
            }
          />

          <Grid item container justifyContent="flex-end">
            <AppButton
              label="Publish"
              type="submit"
              color="primary"
              isLoading={isLoadingPublishVideo}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </AppLoadingAndErrorWrapper>
  );
}
